/**
 * Ad attribution (gclid / msclkid / fbclid / utm_*) capture.
 *
 * The params only ever appear on the landing URL of an ad click. They used to live in
 * the auth store alone, so a reload, a direct link or an entry point that never saw the
 * tagged URL lost the gclid, and the contact info saved by the lookup flow was written
 * without it. Reading straight off `window.location` (the Next router leaves `query`
 * empty on statically optimized pages until it hydrates) and keeping a copy in local
 * storage makes the capture survive both.
 */
import * as LocalStorage from '~/utilities/localStorage'

export const ATTRIBUTION_STORAGE_KEY = 'adAttribution'

// Google Ads attributes a conversion to a click for up to 90 days.
export const MAX_ATTRIBUTION_AGE_MS = 90 * 24 * 60 * 60 * 1000

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_adgroup',
  'utm_keyword',
  'utm_term',
  'utm_content',
]

/** Read the query string from the browser URL, mirroring the shape of `Router.query`. */
export const readQueryFromLocation = () => {
  if (typeof window === 'undefined' || !window.location) return {}
  const query = {}
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (query[key] === undefined) {
      query[key] = value
    } else if (Array.isArray(query[key])) {
      query[key].push(value)
    } else {
      query[key] = [query[key], value]
    }
  })
  return query
}

/**
 * A repeated query param reads back as an array, but the stores hold these as strings
 * (`stores/authStore.js`, `stores/models/User.js`), so keep one occurrence only. A
 * tracking template that writes the param empty and lets the final URL suffix append the
 * real value puts the empty one first, hence the first *filled* occurrence.
 */
const firstValue = value => (Array.isArray(value) ? value.find(Boolean) : value)

/** Pluck the ad attribution out of a query object. Missing pieces are left out. */
export const collectAttributionFromQuery = (query = {}) => {
  const utm = {}
  UTM_KEYS.forEach(key => {
    const value = firstValue(query[key])
    if (value) utm[key] = value
  })
  const gclid = firstValue(query.gclid)
  const msclkid = firstValue(query.msclkid)
  const fbclid = firstValue(query.fbclid)
  // Persist inferred Meta source into stored/FSCI utm (Close also invents at merge).
  // Meta-only: a Google landing shared on Facebook is ?gclid=&fbclid= — do not stamp Meta.
  if (fbclid && !gclid && !msclkid && !utm.utm_source) utm.utm_source = 'meta'

  return {
    ...(gclid ? { gclid } : {}),
    ...(msclkid ? { msclkid } : {}),
    ...(fbclid ? { fbclid } : {}),
    ...(Object.keys(utm).length ? { utm: { datetime: new Date().toISOString(), ...utm } } : {}),
  }
}

/**
 * Landing on the same campaign again keeps the datetime of the visit that first brought
 * it, so a reload of the tagged URL is not written back to the User as a change.
 */
const keepFirstSeen = (utm, storedUtm) => {
  if (!storedUtm || !storedUtm.datetime) return utm
  const sameCampaign = UTM_KEYS.every(key => utm[key] === storedUtm[key])
  return sameCampaign ? { ...utm, datetime: storedUtm.datetime } : utm
}

/**
 * Compare two utm blocks by content: the copy held on the User comes back from Parse in
 * whatever key order it was stored in, so comparing serialized forms would read a plain
 * reorder as a change and rewrite the User on every page load.
 */
export const isSameUtm = (utm, otherUtm) => {
  const left = utm || {}
  const right = otherUtm || {}
  const keys = new Set([...Object.keys(left), ...Object.keys(right)])
  return [...keys].every(key => left[key] === right[key])
}

/** Read the stored attribution, discarding it once it falls outside the click window. */
export const loadStoredAttribution = () => {
  let stored
  try {
    stored = LocalStorage.get(ATTRIBUTION_STORAGE_KEY, true)
  } catch (error) {
    return {}
  }
  if (!stored || typeof stored !== 'object') return {}

  const savedAt = Date.parse(stored.savedAt)
  if (!savedAt || Date.now() - savedAt > MAX_ATTRIBUTION_AGE_MS) {
    LocalStorage.remove(ATTRIBUTION_STORAGE_KEY)
    return {}
  }
  return stored
}

/**
 * Persist an attribution, stamping it so it can be expired later.
 * @param {object} attribution The attribution to save
 * @param {string} savedAt When the click it comes from was first seen, so a later visit
 * carrying the same params does not keep pushing the expiry out.
 */
export const storeAttribution = (attribution, savedAt = new Date().toISOString()) => {
  const payload = { ...attribution, savedAt }
  try {
    LocalStorage.set(ATTRIBUTION_STORAGE_KEY, payload)
  } catch (error) {
    // Storage is full or blocked: the attribution still stands for this page load.
  }
  return payload
}

/**
 * Attribution for the current page load: whatever the URL carries wins, anything it does
 * not carry falls back to the copy saved on an earlier load.
 * @param {object} routerQuery Next router query, merged under the URL params.
 */
export const resolveAttribution = (routerQuery = {}) => {
  const query = { ...routerQuery, ...readQueryFromLocation() }
  const fromUrl = collectAttributionFromQuery(query)
  const stored = loadStoredAttribution()
  // A new click id starts a new attribution: keeping the stored utm block would pair the
  // campaign of the previous click with the id of this one.
  const isNewClick =
    (fromUrl.gclid && fromUrl.gclid !== stored.gclid) ||
    (fromUrl.msclkid && fromUrl.msclkid !== stored.msclkid) ||
    (fromUrl.fbclid && fromUrl.fbclid !== stored.fbclid)
  const merged = isNewClick
    ? fromUrl
    : { ...stored, ...fromUrl, ...(fromUrl.utm ? { utm: keepFirstSeen(fromUrl.utm, stored.utm) } : {}) }

  if (!merged.gclid && !merged.msclkid && !merged.fbclid && !merged.utm) return {}
  if (!Object.keys(fromUrl).length) return merged
  // Only a new click resets the 90 day window; the same params seen again do not.
  return storeAttribution(merged, isNewClick ? undefined : stored.savedAt)
}

/**
 * Resolve the normalized acquisition channel ('ads'|'email'|'sms'|'organic') from an
 * attribution object.
 *
 * Last-touch between paid and lifecycle channels: a fresh email/sms `utm_medium` wins over
 * a paid click id only when that touch is the more recent one — its `utm.datetime` is at or
 * after `gclidCapturedAt`. This stops a stale ads click id (held in local storage for the
 * 90-day Google window) from masking a later email/sms landing, while a paid click arriving
 * afterwards (which resets `gclidCapturedAt` and drops the stored utm) still counts as
 * 'ads'. The click id itself is preserved for the Google Ads conversion upload, which is
 * independent of this reporting channel.
 *
 * Precedence: fresh email/sms utm => email/sms, else any click id => 'ads', else utm_medium
 * email/sms (with no competing click), else 'organic'.
 */
export const resolveChannel = ({ gclid, msclkid, utm, gclidCapturedAt } = {}) => {
  const medium = String((utm && utm.utm_medium) || '').toLowerCase()
  const utmAt = utm && utm.datetime ? Date.parse(utm.datetime) : 0
  const gclidAt = gclidCapturedAt ? Date.parse(gclidCapturedAt) : 0
  // A lifecycle touch (email/sms) outranks the paid click id only when it is the more
  // recent one. Without a utm datetime we cannot rank the two and defer to the click id.
  if ((medium === 'email' || medium === 'sms') && utmAt && (!gclidAt || utmAt >= gclidAt)) {
    return medium
  }
  if (gclid || msclkid) return 'ads'
  if (medium === 'email') return 'email'
  if (medium === 'sms') return 'sms'
  return 'organic'
}
